import { MOBILE_QUERY } from '../../hooks/useMobile';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { MAP_OVERLAY_Z } from '../map/overlay';
import { StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ScrollStrip } from '../ui/ScrollStrip';
import { VISIT_STATUS, display } from '../status';
import { bookedAt } from '../../lib/format';
import {
  CREW_STATE_LABEL,
  CREW_STATE_TONE,
  firstName,
  type CrewTechSource,
} from './crewPins';
import {
  PANEL_TABS,
  SHARED_JOB_NOTE,
  inTab,
  jobFact,
  tabCounts,
  tabOf,
  techNow,
  techTimeline,
  type PanelJob,
  type PanelTab,
  type TimelineStop,
} from './crewFacts';
import { Face } from './Face';

/**
 * The panel's width from `lg` up: ONE value. The map's box sets it as `--crew-panel-w`, and
 * the panel, the floating column beside it and the empty state all read the variable.
 */
export const PANEL_WIDTH = '26rem';

/** The page's selection, and what the panel's rows can ask of it. */
export interface PanelSelection {
  /** The selected visit: a pin or a row. */
  selectedId: string | null;
  /** A row's body: select its job on the map (no preview, no refit). */
  onSelect: (visitId: string) => void;
  /** "Open job": the page's job preview. */
  onOpen: (visitId: string) => void;
  /** "<Name>'s day" and "Back to everyone". */
  onPick: (userId: string | null) => void;
}

/** Each row's element by visit, so the selected one can be scrolled to. */
type RowRef = (visitId: string) => (el: HTMLElement | null) => void;

/**
 * Below `lg` the panel is a bottom sheet; a browser without `matchMedia` counts as narrow. The
 * app's one breakpoint query (`MOBILE_QUERY`, rem), the same the map reads to go inert under
 * the expanded sheet: two spellings of `lg` disagree at a root font size other than 16 px.
 */
const isNarrow = () => {
  const media = globalThis.matchMedia;
  return typeof media !== 'function' || media(MOBILE_QUERY).matches;
};

/**
 * The crew map's right panel (ops insights Phase 4, ticket 04, spec §3).
 *
 * - **Nobody picked:** "Today's appointments", tabbed All / In progress / Up next / Unassigned
 *   / Done with counts that add up (canceled visits listed apart under All), each row with one
 *   fact and the actions "Open job" (the page's job preview) and "<Name>'s day".
 * - **A tech picked:** their face, name and state, what they are doing now, and their day in
 *   order (the numbers their pins carry while they are picked), with "Back to everyone".
 *
 * ⭐ **Selection both ways, one value.** A row's body selects its job on the map (the pin
 * ringed, never a refit), and the page's selection, from a pin or a row, marks the row
 * (`aria-current`) and scrolls it into view, switching to All when the open tab lacks it. (A
 * pin outside a picked tech's day returns the panel to everyone first: `CrewMapTab`.)
 *
 * ⭐ **One element, two shapes.** From `lg` a panel down the right of the map; below, a bottom
 * sheet whose peek shows the title and the tabs, expanded by its handle or a tab, collapsed by
 * the handle or Escape (the focus then lands on the handle). Both sit inside the map's box,
 * above Leaflet (`MAP_OVERLAY_Z`), so neither reaches the phone's tab bar.
 *
 * ⚠️ All / In progress / Up next / Unassigned / Done FILTER one list, so they are pressed
 * buttons (`aria-pressed`, each `aria-controls` the list), not tabs: a tablist promises a
 * panel per tab, and every choice here shows the same rows, fewer of them.
 */
export function CrewPanel({
  jobs,
  techs,
  date,
  tz,
  picked,
  selection,
  expanded,
  onExpanded,
  panelRef,
}: {
  jobs: readonly PanelJob[];
  techs: readonly CrewTechSource[];
  /** The shop's today, `YYYY-MM-DD`. */
  date: string;
  tz: string;
  picked: string | null;
  selection: PanelSelection;
  /**
   * The phone's sheet is expanded. The MAP's state: an expanded sheet takes the room the
   * floating cards and chips stood in, so the map hides them meanwhile.
   */
  expanded: boolean;
  onExpanded: (expanded: boolean) => void;
  /** The panel's own element, measured by the map for its fit and its attribution. */
  panelRef?: (el: HTMLElement | null) => void;
}) {
  const { selectedId } = selection;
  const [tab, setTab] = useState<PanelTab>('all');
  const byId = useMemo(() => new Map(jobs.map((j) => [j.visit.id, j])), [jobs]);
  const tech = techs.find((t) => t.userId === picked) ?? null;

  /*
   * A selection the open tab does not hold switches to All, so a pin's row is always there to
   * mark. Recorded during render (React's "information from previous renders"), and only when
   * the selection CHANGES: choosing another tab afterwards is the reader's to make.
   */
  const [seen, setSeen] = useState(selectedId);
  if (seen !== selectedId) {
    setSeen(selectedId);
    const job = selectedId === null ? undefined : byId.get(selectedId);
    if (job && !inTab(job, tab)) setTab('all');
  }

  const rows = useRef(new Map<string, HTMLElement>());
  const rowRef: RowRef = (visitId) => (el) => {
    if (el) rows.current.set(visitId, el);
    else rows.current.delete(visitId);
  };
  useEffect(() => {
    if (selectedId === null) return;
    const el = rows.current.get(selectedId);
    if (el && typeof el.scrollIntoView === 'function')
      el.scrollIntoView({ block: 'nearest' });
  }, [selectedId, tab, picked]);

  /*
   * ⚠️ Picking from a row, or "Back to everyone", REMOVES the button that was pressed (the
   * panel changes mode), and the browser drops the focus to <body>: a keyboard reader would
   * start again from the top of the page. So on a change of mode, when the focus was in the
   * panel or has just been lost, it moves to the new heading. A pick from a chip or a face
   * keeps the focus where it is.
   */
  const section = useRef<HTMLElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const handle = useRef<HTMLButtonElement | null>(null);
  const mode = tech?.userId ?? null;
  const shownMode = useRef(mode);
  useEffect(() => {
    if (shownMode.current === mode) return;
    shownMode.current = mode;
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    const lost = !active || active === document.body;
    if (lost || (active && section.current?.contains(active))) heading.current?.focus();
  }, [mode]);
  // Stable, or React detaches and re-attaches it on every render, and the map's measuring
  // would be handed null and back each time.
  const setSection = useCallback(
    (el: HTMLElement | null) => {
      section.current = el;
      panelRef?.(el);
    },
    [panelRef],
  );

  /** Escape collapses an expanded sheet and leaves the focus on its handle, not on <body>. */
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && expanded) {
      e.stopPropagation();
      onExpanded(false);
      handle.current?.focus();
    }
  };

  return (
    <section
      ref={setSection}
      data-probe="crew-panel"
      data-expanded={expanded || undefined}
      aria-label={tech ? `${tech.name}'s day` : "Today's appointments"}
      onKeyDown={onKeyDown}
      className={`pointer-events-auto absolute inset-x-0 bottom-0 ${MAP_OVERLAY_Z} flex flex-col rounded-t-[var(--radius)] border border-border bg-surface shadow-md ${
        expanded ? 'max-lg:h-[55%]' : ''
      } lg:inset-x-auto lg:top-3 lg:right-3 lg:bottom-8 lg:w-[var(--crew-panel-w)] lg:rounded-[var(--radius)]`}
    >
      {/* The sheet's handle: a phone's way to open or close it, besides a tab and Escape. */}
      <button
        ref={handle}
        type="button"
        data-probe="crew-panel-handle"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse the panel' : 'Expand the panel'}
        onClick={() => onExpanded(!expanded)}
        className="flex h-5 w-full shrink-0 items-center justify-center lg:hidden"
      >
        <span aria-hidden="true" className="h-1 w-10 rounded-full bg-border" />
      </button>

      {tech ? (
        <TechDay
          tech={tech}
          byId={byId}
          tz={tz}
          date={date}
          expanded={expanded}
          selection={selection}
          rowRef={rowRef}
          headingRef={heading}
        />
      ) : (
        <Appointments
          jobs={jobs}
          techs={techs}
          byId={byId}
          tz={tz}
          date={date}
          tab={tab}
          onTab={(next) => {
            setTab(next);
            // On a phone, choosing a tab asks to see it; at `lg` there is nothing to open.
            if (isNarrow()) onExpanded(true);
          }}
          expanded={expanded}
          selection={selection}
          rowRef={rowRef}
          headingRef={heading}
        />
      )}
    </section>
  );
}

/** The scrolling body: hidden below `lg` while the sheet only peeks. */
const body = (expanded: boolean) =>
  `min-h-0 flex-1 overflow-y-auto px-3 pb-3 ${expanded ? '' : 'max-lg:hidden'}`;

/** A row's body, selecting its job on the map: the marked one ringed. */
const ROW_BODY =
  'rounded-[var(--radius-sm)] px-1.5 py-1 text-left hover:bg-surface-2 aria-[current=true]:bg-surface-2 aria-[current=true]:ring-1 aria-[current=true]:ring-brand';

function Footnote() {
  return (
    <p data-probe="crew-panel-note" className="mt-3 text-xs text-muted-foreground">
      {SHARED_JOB_NOTE}
    </p>
  );
}

function Appointments({
  jobs,
  techs,
  byId,
  tz,
  date,
  tab,
  onTab,
  expanded,
  selection,
  rowRef,
  headingRef,
}: {
  jobs: readonly PanelJob[];
  techs: readonly CrewTechSource[];
  byId: ReadonlyMap<string, PanelJob>;
  tz: string;
  date: string;
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  expanded: boolean;
  selection: PanelSelection;
  rowRef: RowRef;
  headingRef: Ref<HTMLHeadingElement>;
}) {
  const listId = useId();
  const counts = tabCounts(jobs);
  /** Only people on the roster get a "<Name>'s day": the pick names a chip. */
  const onRoster = new Set(techs.map((t) => t.userId));
  const row = (j: PanelJob) => (
    <AppointmentRow
      key={j.visit.id}
      job={j}
      fact={jobFact(j, techs, byId, tz, date)}
      when={bookedAt(j.visit.scheduledStartAt, tz, date)}
      crew={j.assignees.filter((a) => onRoster.has(a.userId))}
      selection={selection}
      rowRef={rowRef}
    />
  );
  const shown = jobs.filter((j) => tabOf(j) !== null && inTab(j, tab));
  const canceled = tab === 'all' ? jobs.filter((j) => tabOf(j) === null) : [];
  const label = PANEL_TABS.find((t) => t.key === tab)?.label ?? '';
  return (
    <>
      <div className="shrink-0 px-3 pt-1 lg:pt-3">
        <h2 ref={headingRef} tabIndex={-1} className="font-heading text-sm font-semibold">
          Today&apos;s appointments
        </h2>
        {/*
          A FILTER of the one list below, so pressed buttons, not tabs: every choice shows the
          same rows, fewer of them, in the one list each button controls.
        */}
        <div className="mt-1">
          <ScrollStrip bordered>
            <div role="group" aria-label="Show" className="flex min-w-max gap-5">
              {PANEL_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  data-panel-tab={t.key}
                  aria-pressed={tab === t.key}
                  aria-controls={listId}
                  onClick={() => onTab(t.key)}
                  className="border-b-2 border-transparent pb-2 text-sm font-medium whitespace-nowrap text-muted-foreground hover:text-text aria-pressed:border-brand aria-pressed:text-[var(--sw-brand-text)]"
                >
                  {t.label}{' '}
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {counts[t.key]}
                  </span>
                </button>
              ))}
            </div>
          </ScrollStrip>
        </div>
      </div>
      <div className={body(expanded)}>
        <div
          id={listId}
          data-panel-list={tab}
          aria-label={`${label} appointments`}
          role="region"
        >
          {shown.length === 0 ? (
            <p className="py-3 text-xs text-muted-foreground">
              {jobs.length === 0 ? 'No visits today.' : 'Nothing here right now.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">{shown.map(row)}</ul>
          )}
          {/* Canceled: in no count above, listed apart under All with their own. */}
          {canceled.length > 0 ? (
            <>
              <p
                data-probe="crew-canceled"
                className="mt-2 border-t border-border pt-2 text-xs font-semibold text-muted-foreground"
              >
                {canceled.length} canceled
              </p>
              <ul className="divide-y divide-border">{canceled.map(row)}</ul>
            </>
          ) : null}
        </div>
        <Footnote />
      </div>
    </>
  );
}

/** One of today's appointments: its body selects it on the map; its actions open or pick. */
function AppointmentRow({
  job: j,
  fact,
  when,
  crew,
  selection,
  rowRef,
}: {
  job: PanelJob;
  fact: string;
  when: string;
  /** The assignees on the roster: one "<Name>'s day" each. */
  crew: { userId: string; name: string }[];
  selection: PanelSelection;
  rowRef: RowRef;
}) {
  const s = display(VISIT_STATUS, j.visit.status);
  return (
    <li ref={rowRef(j.visit.id)} data-panel-row={j.visit.id} className="py-2">
      <button
        type="button"
        onClick={() => selection.onSelect(j.visit.id)}
        aria-current={j.visit.id === selection.selectedId ? 'true' : undefined}
        className={`block w-full ${ROW_BODY}`}
      >
        <span className="sr-only">Show on the map: </span>
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-xs tabular-nums text-muted-foreground">
            {when}
            {j.job.serviceType ? ` · ${j.job.serviceType}` : ''}
          </span>
          <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
        </span>
        <span className="block truncate text-sm font-medium">{j.job.title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {[j.customer.displayName, j.property.city].filter(Boolean).join(' · ')}
        </span>
        <span data-probe="crew-fact" className="mt-0.5 block text-xs">
          {fact}
        </span>
      </button>
      <span className="mt-1 flex flex-wrap gap-1.5 px-1.5">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Open job: ${j.job.title}`}
          onClick={() => selection.onOpen(j.visit.id)}
        >
          Open job
        </Button>
        {crew.map((a) => (
          <Button
            key={a.userId}
            variant="ghost"
            size="sm"
            data-day-of={a.userId}
            onClick={() => selection.onPick(a.userId)}
          >
            {firstName(a.name)}&apos;s day
          </Button>
        ))}
      </span>
    </li>
  );
}

function TechDay({
  tech,
  byId,
  tz,
  date,
  expanded,
  selection,
  rowRef,
  headingRef,
}: {
  tech: CrewTechSource;
  byId: ReadonlyMap<string, PanelJob>;
  tz: string;
  date: string;
  expanded: boolean;
  selection: PanelSelection;
  rowRef: RowRef;
  headingRef: Ref<HTMLHeadingElement>;
}) {
  const now = techNow(tech, byId, tz, date);
  const stops = techTimeline(tech, byId, tz, date);
  return (
    <>
      <div className="flex shrink-0 items-center gap-2 px-3 pt-1 pb-2 lg:pt-3">
        <Face text={tech.initials} tone={CREW_STATE_TONE[tech.state]} size="md" />
        <div className="min-w-0 flex-1">
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="truncate font-heading text-sm font-semibold"
          >
            {tech.name}
          </h2>
          <p className="text-xs text-muted-foreground">
            {CREW_STATE_LABEL[tech.state] ?? tech.state}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          data-probe="crew-back"
          className="shrink-0"
          onClick={() => selection.onPick(null)}
        >
          Back to everyone
        </Button>
      </div>
      <div className={body(expanded)}>
        <div
          data-probe="crew-now"
          className="rounded-[var(--radius-sm)] bg-surface-2 p-2 text-sm"
        >
          {now.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
        <h3 className="mt-3 text-xs font-semibold text-muted-foreground">Their day</h3>
        {stops.length === 0 ? (
          <p className="py-2 text-xs text-muted-foreground">Nothing booked today.</p>
        ) : (
          <ol className="mt-1 space-y-1">
            {stops.map((s) => (
              <StopRow key={s.visitId} stop={s} selection={selection} rowRef={rowRef} />
            ))}
          </ol>
        )}
        <Footnote />
      </div>
    </>
  );
}

/** One stop of a picked tech's day, numbered as their pin is while they are picked. */
function StopRow({
  stop: s,
  selection,
  rowRef,
}: {
  stop: TimelineStop;
  selection: PanelSelection;
  rowRef: RowRef;
}) {
  const st = display(VISIT_STATUS, s.status);
  return (
    <li
      ref={rowRef(s.visitId)}
      data-panel-row={s.visitId}
      className="flex items-start gap-2"
    >
      <button
        type="button"
        onClick={() => selection.onSelect(s.visitId)}
        aria-current={s.visitId === selection.selectedId ? 'true' : undefined}
        className={`${ROW_BODY} flex min-w-0 flex-1 items-start gap-2`}
      >
        <span className="sr-only">
          {s.order === null ? 'Show on the map, ' : `Show on the map: stop ${s.order}, `}
        </span>
        {/* A canceled visit is not a stop: an empty disc keeps the column, with no number. */}
        <span
          aria-hidden="true"
          className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface-2 text-[10px] font-semibold tabular-nums ring-1 ring-border"
        >
          {s.order}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">{s.customer}</span>
            <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
          </span>
          <span className="block truncate text-xs text-muted-foreground">{s.title}</span>
          <span data-probe="crew-stop" className="block text-xs tabular-nums">
            {s.line}
          </span>
        </span>
      </button>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1 shrink-0"
        aria-label={`Open job: ${s.title}`}
        onClick={() => selection.onOpen(s.visitId)}
      >
        Open job
      </Button>
    </li>
  );
}
